import { Box } from '@mui/material';
import { isExpandableTable } from 'constants/graphql-typenames';
import { lazy, ReactNode, Suspense, useRef } from 'react';
import { ExpandableTablePlaceholder as ExpandableTablePlaceholderNew } from 'components/ExpandableTable/ExpandableTablePlaceholder';
import { updateExpandableTableFields } from 'utils/table';
import { ExpandableTable as ExpandableTableType, TablesScreen as TablesScreenType } from 'generated/graphql';

const ExpandableTable = lazy(() => import('components/ExpandableTable/ExpandableTable'));

function updateTableInScreen(
  screen: TablesScreenType,
  updatedTable: ExpandableTableType,
  tabIndex: number,
  groupIndex: number,
  tableIndex: number,
): TablesScreenType {
  return {
    ...screen,
    tabs: screen.tabs.map((tab, ti) => ({
      ...tab,
      tableGroups: tab.tableGroups.map((group, gi) => {
        if (ti !== tabIndex || gi !== groupIndex) return group;
        const data = group.data ?? [];
        return {
          ...group,
          data: data.map((item, i) => (i === tableIndex && isExpandableTable(item) ? updatedTable : item)),
        };
      }),
    })),
  };
}

export enum TableScreenQueryParam {
  Tab = 't',
  Group = 'g',
  Input = 'i',
  Search = 's',
}

const TableWrapper = ({
  table,
  isRevalidating,
  screen,
  index,
  tabIndex,
  tableGroupIndex,
  header,
  onRefresh,
  onScreenUpdate,
}: {
  table: ExpandableTableType;
  tables: ExpandableTableType[];
  screen?: TablesScreenType;
  isRevalidating: boolean;
  index: number;
  tabIndex: number;
  tableGroupIndex: number;
  header: (actions: ReactNode) => ReactNode;
  onRefresh: () => void;
  onScreenUpdate: (screen: TablesScreenType) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const handleFieldUpdate = (rowId: string, cellIndex: number, newValue: string) => {
    const updatedTable = updateExpandableTableFields(table, cellIndex, rowId, newValue);

    if (screen) {
      const updatedScreen = updateTableInScreen(screen, updatedTable, tabIndex, tableGroupIndex, index);
      onScreenUpdate(updatedScreen);
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={(theme) => ({
        marginTop: theme.spacing(1),
      })}
    >
      <Suspense fallback={<ExpandableTablePlaceholderNew />}>
        <ExpandableTable
          table={table}
          header={header}
          fullBleedOnMobile
          isFetching={isRevalidating}
          onRefresh={onRefresh}
          onFieldUpdate={handleFieldUpdate}
        />
      </Suspense>
    </Box>
  );
};

export default TableWrapper;
